import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Check, Plus, Trash2, Printer, FileBarChart, UserPlus } from "lucide-react";

const TAX_OPTIONS = [
  ["differenz", "§25a Differenzbesteuerung (Standard)"],
  ["regel", "19 % Umsatzsteuer"],
  ["frei", "0 % / steuerfrei"],
  ["ku19", "§19 UStG / Kleinunternehmer"],
];

export function VerkaufAusAnkauf({ purchase, open, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [lines, setLines] = useState({});
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: "", last_name: "", email: "" });
  const [taxMode, setTaxMode] = useState("differenz");
  const [paymentMethod, setPaymentMethod] = useState("bar");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [shipping, setShipping] = useState("");
  const [costs, setCosts] = useState([]);
  const [note, setNote] = useState("");
  const [taxRate, setTaxRate] = useState(19);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !purchase) return;
    setResult(null);
    setLines({});
    setCosts([]);
    setShipping("");
    setNote("");
    api.get(`/purchases/${purchase.id}/sellable`).then((r) => {
      setData(r.data);
      const init = {};
      r.data.lines.forEach((l) => {
        if (l.qty_available > 0) init[l.product_id] = { qty: 1, sale_price: l.articles[0]?.sale_price || "" };
      });
      setLines(init);
    }).catch((e) => toast.error(apiError(e)));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/settings").then((r) => {
      const d = r.data.tenant?.tax_mode_default;
      if (["differenz", "regel", "frei", "ku19"].includes(d)) setTaxMode(d);
      if (r.data.tenant?.tax_rate) setTaxRate(Number(r.data.tenant.tax_rate));
    }).catch(() => {});
  }, [open, purchase]);

  const activeLines = (data?.lines || []).filter((l) => lines[l.product_id] && lines[l.product_id].qty > 0);
  const total = activeLines.reduce((s, l) => s + (parseFloat(lines[l.product_id].sale_price) || 0) * lines[l.product_id].qty, 0);
  const costTotal = activeLines.reduce((s, l) => s + l.purchase_price * lines[l.product_id].qty, 0);
  const extra = (parseFloat(shipping) || 0) + costs.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const margin = Math.max(0, total - costTotal);
  const rf = 1 + taxRate / 100;
  const taxPreview = taxMode === "differenz" ? margin - margin / rf : taxMode === "regel" ? total - total / rf : 0;
  const profitAfterTax = taxMode === "regel" ? total / rf - costTotal - extra : margin - taxPreview - extra;

  const createCustomer = async () => {
    try {
      const { data: c } = await api.post("/customers", newCust);
      setCustomers([c, ...customers]);
      setCustomerId(c.id);
      setNewCustOpen(false);
      toast.success(`Kunde ${c.number} angelegt.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data: sale } = await api.post(`/purchases/${purchase.id}/sell`, {
        customer_id: customerId, tax_mode: taxMode, payment_method: paymentMethod, date,
        shipping: parseFloat(shipping) || 0,
        costs: costs.filter((c) => parseFloat(c.amount) > 0).map((c) => ({ label: c.label, amount: parseFloat(c.amount) })),
        note,
        lines: activeLines.map((l) => ({ product_id: l.product_id, name: l.name, qty: lines[l.product_id].qty, sale_price: parseFloat(lines[l.product_id].sale_price) || 0 })),
      });
      setResult(sale);
      toast.success(`Verkauf ${sale.number} gebucht · Rechnung ${sale.invoice.number} · Gewinn nach Steuer ${eur(sale.profit_after_tax)}`);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const openPdf = async (path) => {
    const { data: blob } = await api.get(path, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([blob], { type: "application/pdf" })), "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sell-from-purchase-dialog">
        <DialogHeader>
          <DialogTitle>Als Verkauf erfassen · {purchase?.number}</DialogTitle>
        </DialogHeader>
        {!result ? (
          <div className="space-y-5 text-sm">
            {data?.lines.map((l) => (
              <div key={l.product_id} className="border border-zinc-200 rounded-xl p-4 space-y-3" data-testid={`sell-line-${l.product_id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{l.name}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-0.5">
                      Eingekauft: {l.qty_total} Stück · EK {eur(l.purchase_price)}/Stück · verfügbar: {l.qty_available}
                    </div>
                  </div>
                  {l.qty_available === 0 && <span className="text-[10px] font-mono bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">verkauft</span>}
                </div>
                {l.qty_available > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Verkaufspreis / Stück (€)</Label>
                      <Input type="number" step="0.01" value={lines[l.product_id]?.sale_price ?? ""}
                        onChange={(e) => setLines({ ...lines, [l.product_id]: { ...lines[l.product_id], sale_price: e.target.value } })}
                        data-testid={`sell-price-${l.product_id}`} className="mt-1.5" />
                    </div>
                    <div>
                      <Label>Verkaufte Menge (max. {l.qty_available})</Label>
                      <Input type="number" min="1" max={l.qty_available} value={lines[l.product_id]?.qty ?? 1}
                        onChange={(e) => setLines({ ...lines, [l.product_id]: { ...lines[l.product_id], qty: Math.min(l.qty_available, Math.max(1, parseInt(e.target.value) || 1)) } })}
                        data-testid={`sell-qty-${l.product_id}`} className="mt-1.5" />
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Kunde</Label>
                <div className="flex gap-2 mt-1.5">
                  <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="sell-customer-select"
                    className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]">
                    <option value="">— Kunde auswählen —</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company ? `· ${c.company}` : ""} ({c.number})</option>)}
                  </select>
                  <Button variant="outline" onClick={() => setNewCustOpen(true)} data-testid="sell-new-customer" className="min-h-[44px]"><UserPlus className="w-4 h-4" /></Button>
                </div>
              </div>
              <div><Label>Verkaufsdatum</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sell-date" className="mt-1.5" /></div>
              <div><Label>Zahlungsart</Label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="sell-payment" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]">
                  {["bar", "überweisung", "karte", "paypal"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-2"><Label>Steuerart</Label>
                <select value={taxMode} onChange={(e) => setTaxMode(e.target.value)} data-testid="sell-tax-mode" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]">
                  {TAX_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div><Label>Versandkosten (€)</Label><Input type="number" step="0.01" value={shipping} onChange={(e) => setShipping(e.target.value)} data-testid="sell-shipping" className="mt-1.5" /></div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Zusätzliche Kosten (Verpackung, Reparatur, Ersatzteile…)</Label>
                <Button variant="outline" size="sm" onClick={() => setCosts([...costs, { label: "", amount: "" }])} data-testid="sell-add-cost"><Plus className="w-3.5 h-3.5 mr-1" /> Kosten</Button>
              </div>
              {costs.map((c, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Bezeichnung" value={c.label} onChange={(e) => setCosts(costs.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} data-testid={`sell-cost-label-${i}`} />
                  <Input type="number" step="0.01" placeholder="Betrag €" value={c.amount} onChange={(e) => setCosts(costs.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))} className="w-32" data-testid={`sell-cost-amount-${i}`} />
                  <Button variant="outline" size="sm" onClick={() => setCosts(costs.filter((_, xi) => xi !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>

            <div><Label>Bemerkung</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="sell-note" className="mt-1.5" /></div>

            <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/50 space-y-1" data-testid="sell-summary">
              <div className="flex justify-between"><span className="text-zinc-500">Verkaufserlös</span><b>{eur(total)}</b></div>
              <div className="flex justify-between"><span className="text-zinc-500">Einkaufskosten (anteilig)</span><span>{eur(costTotal)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Zusatzkosten</span><span>{eur(extra)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Steuer ({TAX_OPTIONS.find(([k]) => k === taxMode)?.[1]})</span><span>{eur(taxPreview)}</span></div>
              <div className="flex justify-between border-t border-zinc-200 pt-2 font-medium"><span>Gewinn nach Steuer</span><span data-testid="sell-profit-preview">{eur(profitAfterTax)}</span></div>
            </div>

            <Button onClick={submit} disabled={busy || !customerId || activeLines.length === 0 || total <= 0}
              data-testid="sell-submit-button" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white min-h-[48px]">
              {busy ? "Buche…" : "Verkauf abschließen & Rechnung erstellen"} <Check className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : (
          <div className="text-center space-y-4 py-4" data-testid="sell-success">
            <div className="w-12 h-12 bg-zinc-900 text-white rounded-full flex items-center justify-center mx-auto"><Check className="w-6 h-6" /></div>
            <h3 className="font-display text-xl font-semibold">Verkauf {result.number} abgeschlossen</h3>
            <p className="text-sm text-zinc-500">
              Rechnung {result.invoice.number} · Bestand reduziert · Gewinn nach Steuer <b>{eur(result.profit_after_tax)}</b>
            </p>
            <div className="flex gap-2 justify-center flex-wrap">
              <Button variant="outline" onClick={() => openPdf(`/invoices/${result.invoice.id}/pdf`)} data-testid="sell-invoice-pdf" className="min-h-[44px]">
                <Printer className="w-4 h-4 mr-1" /> Kundenrechnung
              </Button>
              <Button variant="outline" onClick={() => navigate(`/app/verkauf/${result.id}/beleg`)} data-testid="sell-buchhaltung-pdf" className="min-h-[44px] border-zinc-900">
                <FileBarChart className="w-4 h-4 mr-1" /> Buchhaltungsbeleg (INTERN)
              </Button>
            </div>
            <Button variant="ghost" onClick={onClose} data-testid="sell-close">Schließen</Button>
          </div>
        )}

        <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
          <DialogContent data-testid="sell-new-customer-dialog">
            <DialogHeader><DialogTitle>Neuer Kunde</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Vorname</Label><Input value={newCust.first_name} onChange={(e) => setNewCust({ ...newCust, first_name: e.target.value })} className="mt-1.5" data-testid="sell-nc-first" /></div>
              <div><Label>Nachname</Label><Input value={newCust.last_name} onChange={(e) => setNewCust({ ...newCust, last_name: e.target.value })} className="mt-1.5" data-testid="sell-nc-last" /></div>
              <div><Label>E-Mail</Label><Input type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} className="mt-1.5" data-testid="sell-nc-email" /></div>
              <Button onClick={createCustomer} disabled={!newCust.last_name} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="sell-nc-save">Anlegen & übernehmen</Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
